<script setup lang="ts">
import SelectItem from "@/app/components/SelectItem.vue"
import { useCurrencies } from "@/app/composables/useCurrencies"
import { t } from "@/app/composables/useI18n"
import { useTransactions } from "@/app/composables/useTransactions"
import { vClickOutside } from "@/app/directives"
import { cfl, formatLocalDateTime } from "@/app/utils/strings"
import { onMounted } from "vue"

const { getIconByTicker } = useCurrencies()
const {
  loading,
  showMoreButton,
  transactions,
  wallets,
  isWalletOpen,
  isStatusOpen,
  isTypeOpen,
  selectedStatus,
  selectedWallet,
  selectedType,
  statuses,
  types,
  allWalletOption,
  closeAllFilters,
  openWalletFilter,
  openStatusFilter,
  openTypeFilter,
  selectWallet,
  selectStatus,
  selectType,
  loadTransactions,
  loadNextPage,
  loadUserWallets,
} = useTransactions()

function getWalletTitle(wallet: any): string {
  if (wallet.id === null) {
    return t("finance.transactions_all")
  }
  return `${wallet.currency?.ticker}: ${cfl(wallet.type || "")}`
}

onMounted(() => {
  loadUserWallets()
  loadTransactions()
})
</script>

<template>
  <div class="transaction" data-testid="transaction-list">
    <div class="transaction-filters">
      <div v-click-outside="closeAllFilters" class="transaction-filter">
        <div>{{ t("account.wallet") }}</div>
        <div class="select-filter" @click="openWalletFilter">
          <SelectItem :value="getWalletTitle(selectedWallet)" :is-open="isWalletOpen" name="wallet">
            <template #header>
              <div :key="allWalletOption.id" class="select-filter__item" @click="selectWallet(allWalletOption)">
                {{ getWalletTitle(allWalletOption) }}
              </div>
              <div v-for="wallet in wallets" :key="wallet.id" data-testid="selectFilterItem" class="select-filter__item" @click="selectWallet(wallet)">
                {{ getWalletTitle(wallet) }}
              </div>
            </template>
          </SelectItem>
        </div>
      </div>
      <div v-click-outside="closeAllFilters" class="transaction-filter">
        <div>{{ t("finance.status") }}</div>
        <div class="select-filter" @click="openStatusFilter">
          <SelectItem :value="selectedStatus.title" :is-open="isStatusOpen" name="status">
            <template #header>
              <div v-for="status in statuses" :key="status.title" data-testid="selectFilterItem" class="select-filter__item" @click="selectStatus(status)">
                {{ status.title }}
              </div>
            </template>
          </SelectItem>
        </div>
      </div>
      <div v-click-outside="closeAllFilters" class="transaction-filter">
        <div>{{ t("finance.type") }}</div>
        <div class="select-filter" @click="openTypeFilter">
          <SelectItem :value="selectedType.title" :is-open="isTypeOpen" name="type">
            <template #header>
              <div v-for="type in types" :key="type.title" data-testid="selectFilterItem" class="select-filter__item" @click="selectType(type)">
                {{ type.title }}
              </div>
            </template>
          </SelectItem>
        </div>
      </div>
    </div>

    <div v-for="transaction in transactions" :key="transaction.id" class="transaction-card" data-testid="transaction-item">
      <div class="card-header" data-testid="cardHeader">
        <div class="header-left">
          <div>&nbsp;{{ t(`finance.${transaction.type}`) }}</div>
        </div>
        <div :class="`status ${transaction.status}`">
          {{ t(`finance.${transaction.status}`) }}
        </div>
      </div>

      <div class="card-body" data-testid="cardBody">
        <div class="transaction-rows">
          <div class="transaction-row">
            <div class="row-title">
              {{ t("finance.amount") }}
            </div>
            <div class="row-value">
              <img class="img-icon" :src="getIconByTicker(transaction?.wallet?.currency?.ticker)" alt="">
              {{ transaction.amount }}
            </div>
          </div>

          <div v-if="transaction.fee" class="transaction-row">
            <div class="row-title">
              {{ t("finance.fee") }}
            </div>
            <div class="row-value">
              {{ transaction.fee }}
            </div>
          </div>

          <div class="transaction-row">
            <div class="row-title">
              {{ t("account.dob_date") }} & {{ t("finance.time") }}
            </div>
            <div class="row-value">
              {{ formatLocalDateTime(transaction.created_at) }}
            </div>
          </div>

          <div v-if="transaction.type === 'deposit'" class="transaction-row">
            <div class="row-title">
              {{ t("finance.rollover") }}
            </div>
            <div class="row-value rollover-container">
              <div class="rollover-progress">
                <div
                  class="rollover-progress-fill"
                  :style="{
                    width: transaction.spent_amount_percent > 0 ? `${transaction.spent_amount_percent}%` : '0%',
                    minWidth: transaction.spent_amount_percent > 0 ? '30px' : '0',
                  }"
                  :class="{
                    zero: transaction.spent_amount_percent === 0,
                    low: transaction.spent_amount_percent > 0 && transaction.spent_amount_percent < 30,
                    medium: transaction.spent_amount_percent >= 30 && transaction.spent_amount_percent < 70,
                    high: transaction.spent_amount_percent >= 70,
                  }"
                />
                <span
                  class="progress-text"
                  :class="{
                    'text-outside': transaction.spent_amount_percent < 20,
                    'text-zero': transaction.spent_amount_percent === 0,
                  }"
                >
                  {{ transaction.spent_amount_percent }}%
                </span>
              </div>
            </div>
          </div>

          <div v-if="transaction.desc !== null && transaction.desc !== ''" class="transaction-row">
            <div class="row-title">
              {{ t("finance.description") }}
            </div>
            <div class="row-value">
              {{ transaction.desc }}
            </div>
          </div>

          <div v-if="transaction.message !== null" class="transaction-row">
            <div class="row-title">
              {{ t("finance.message") }}
            </div>
            <div class="row-value">
              {{ transaction.message }}
            </div>
          </div>

          <div v-if="transaction.crypto_tx_id !== null" class="transaction-row">
            <div class="row-title">
              TX
            </div>
            <div class="row-value txid">
              {{ transaction.crypto_tx_id }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!showMoreButton && transactions.length === 0 && loading !== true" class="empty-data" data-testid="emptyData">
      {{ t("general.dont_have") }} {{ t("finance.transactions") }}
    </div>

    <button
      v-if="showMoreButton && transactions.length > 0"
      class="btn-g"
      :disabled="loading"
      @click="loadNextPage()"
    >
      <span v-if="loading">{{ t("finance.loading") }}</span>
      <span v-else>{{ t("finance.more_transactions") }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.rollover-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.rollover-progress {
  width: 100%;
  height: 21px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.rollover-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
  position: relative;

  &.zero {
    width: 0 !important;
    min-width: 0;
  }

  &.low {
    background: var(--be-heart);
  }

  &.medium {
    background: var(--be-hot);
  }

  &.high {
    background: #22c55e;
  }
}

.progress-text {
  padding: 0 8px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

  &.text-zero {
    right: 8px;
    color: white;
  }

  &.text-outside {
    color: white;
  }
}

.transaction-rows {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.transaction-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}

.row-title {
  white-space: nowrap;
  flex-shrink: 0;
  max-width: fit-content;
}

.row-value {
  flex-grow: 1;
  text-align: right;
  width: 100%;
}

.card-body {
  width: 100%;
}

.amount-titles, .card--amounts {
  display: none;
}

.img-icon {
  width: 16px;
  height: 16px;
  float: right;
}

.txid {
  width: 260px;
  overflow-y: auto;
}

.btn-g {
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--be-primary-btn);
  margin: 12px 0px;
}

.transaction {
  margin: 16px;
}

.select-item {
  display: flex;
  padding: 10px;
  border-radius: 5px;
  background: var(--be-small-card-bg);
  justify-content: space-between;
}

.transaction-filters {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  .transaction-filter {
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
  }

  .select-filter {
    width: 132px;

    &__item {
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media screen and (max-width: 500px) {
    .select-filter {
      width: 113px;
    }
  }
}

.transaction-card {
  margin-top: 1em;
  border-radius: 8px;
  background: var(--be-big-card-bg);
  font-size: 14px;
  padding: 10px 16px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .header-left {
      display: flex;
    }

    .status {
      border-radius: 4px;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      padding: 6px 8px;
    }

    .success {
      color: #3178d3;
      background: rgba(55, 111, 229, 0.3);
    }
    .initiated {
      background: rgba(72, 72, 72, 0.3);
      color: rgba(255, 255, 255, 0.5);
    }
    .error {
      background: rgba(148, 0, 0, 0.3);
      color: #940000;
    }
    .cancelled {
      background: rgba(147, 52, 1, 0.3);
      color: #943e00;
    }
  }
}
</style>
